import React, { useState, useMemo } from 'react';
import Papa from 'papaparse';
import classnames from 'classnames';
import { connect, batch } from 'react-redux';
import { Button, FormFile } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinusCircle } from '@fortawesome/pro-solid-svg-icons';
import { omit, difference } from 'lodash';

import ToolkitProvider, { CSVExport } from 'react-bootstrap-table2-toolkit';
import BootstrapTable from 'react-bootstrap-table-next';
import filterFactory, { textFilter } from 'react-bootstrap-table2-filter';
import cellEditFactory from 'react-bootstrap-table2-editor';

import {
  deleteLanguage,
  openAddLanguageModal,
  setLanguages,
  addLanguage,
} from '../actions';

import RoundButton from '../../../../components/RoundButton';
import TextArea from './TextAreaEditor';
import { displayErrorModal, displaySuccessModal } from '../../../../helpers/swal';
import HtmlPreviewButton, { looksLikeHtml } from '../HtmlPreviewButton';
import findImportLengthErrors from '../importLengthErrors';

import style from './style.module.scss';

const { ExportCSVButton } = CSVExport;

export const isLanguageRowHidden = (key, {
  geofenceEnabled,
  triviaEnabled,
  enabledPlatforms,
}) => {
  if (!geofenceEnabled && (key.startsWith('I18N_LOCATION_') || key.startsWith('I18N_GEO_HELPER_'))) return true;
  if (!triviaEnabled && key.includes('TRIVIA')) return true;
  if (!enabledPlatforms) return false;
  if (key.startsWith('I18N_SHARE_DOWNLOAD')) return !enabledPlatforms.has('download');
  if (key.startsWith('I18N_SHARE_EMAIL') || key === 'I18N_MULTI_EMAIL_PLACEHOLDER') {
    return !enabledPlatforms.has('email');
  }
  if (key === 'I18N_SHARE_SHARE') {
    return !enabledPlatforms.has('share') && !enabledPlatforms.has('share level 2');
  }
  return false;
};

const headerFormatter = (
  column,
  colIndex,
  { filterElement },
  availableLanguages,
  defaultLanguage,
  deleteLanguageAction,
) => (
  <div className={style.ColumnHeaderContainer}>
    <div className={style.ColumnHeader}>
      <span>{column.text}</span>
      <div>
        {(availableLanguages.length > 1 && column.text !== defaultLanguage) && (
          <RoundButton size="sm" onClick={() => deleteLanguageAction(column.text)}>
            <FontAwesomeIcon icon={faMinusCircle} />
          </RoundButton>
        )}
      </div>
    </div>
    {filterElement}
  </div>
);

const editorRenderer = (editorProps, value, row) => (
  <TextArea {...editorProps} value={value} maxlength={row.html ? null : row.maxLength} />
);

const LanguagesTable = ({
  languages,
  availableLanguages,
  defaultLanguage,
  slugDate,
  geofenceEnabled,
  triviaEnabled,
  shareOptions,
  deleteLanguageAction,
  openAddLanguageModalAction,
  setLanguagesAction,
  addLanguageAction,
}) => {
  // this is used to color code field when importing a new language
  // from csv. Green fiels are updated, red ones didn't have a value before
  const [changesMap, setChangesMap] = useState({});

  const languagesSortedByIndex = useMemo(() => {
    const enabledPlatforms = Array.isArray(shareOptions)
      ? new Set(
        shareOptions
          .filter((option) => option?.enabled && option.platform)
          .map((option) => String(option.platform).toLowerCase()),
      )
      : null;

    return (languages || [])
      .filter((row) => !isLanguageRowHidden(row?.key || '', {
        geofenceEnabled,
        triviaEnabled,
        enabledPlatforms,
      }))
      .sort((a, b) => (Number(a.index) || 0) - (Number(b.index) || 0));
  }, [languages, geofenceEnabled, triviaEnabled, shareOptions]);

  const dynamicColumns = availableLanguages.map((lang) => ({
    text: lang,
    dataField: lang,
    sort: false,
    classes: (cell, row) => classnames('hover-edit-background', {
      [style.modifiedCell]: changesMap[row.key]?.[lang] === 'modified',
      [style.emptyCell]: changesMap[row.key]?.[lang] && changesMap[row.key][lang] !== 'modified',
    }),
    filter: textFilter(),
    headerFormatter: (column, colIndex, filterArgs) => headerFormatter(
      column,
      colIndex,
      filterArgs,
      availableLanguages,
      defaultLanguage,
      deleteLanguageAction,
    ),
    editorRenderer,
    formatter: (cell) => (
      <div className={style.cellWithPreview}>
        <span>{cell}</span>
        {looksLikeHtml(cell) && <HtmlPreviewButton value={cell} />}
      </div>
    ),
  }));

  const columns = [
    {
      text: 'index',
      dataField: 'index',
      sort: true,
      filter: textFilter(),
      classes: classnames('hover-edit-background', style.minWidth),
    },
    {
      text: 'key',
      dataField: 'key',
      hidden: true,
      editable: false,
    },
    {
      text: 'maxLength',
      dataField: 'maxLength',
      hidden: true,
      editable: false,
    },
    {
      text: 'state',
      dataField: 'state',
      sort: true,
      filter: textFilter(),
      editable: false,
      classes: style.minWidth,
    },
    {
      text: 'description',
      dataField: 'description',
      sort: true,
      filter: textFilter(),
      editable: false,
    },
    ...dynamicColumns,
  ];

  const importHandler = async (evt) => {
    const file = evt.target.files[0];
    document.getElementById('file-input').value = '';
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: ({ errors, data }) => {
        if (errors.length > 0) {
          return displayErrorModal({
            html: (
              <>
                <p>Errors:</p>
                {errors.map(({ message, row }) => (
                  <p key={message}>
                    {message}
                    &nbsp;&nbsp;&nbsp;&nbsp;row:&nbsp;
                    {row + 2}
                  </p>
                ))}
              </>
            ),
          });
        }

        const fileLanguages = omit(data[0], ['index', 'key', 'maxLength', 'state', 'description']);
        const fileLanguagesList = Object.keys(fileLanguages);

        const languagesToDelete = difference(availableLanguages, fileLanguagesList);
        const languagesToAdd = difference(fileLanguagesList, availableLanguages);

        const csvLanguageMap = data.reduce((acc, item) => {
          if (item.key) acc[item.key] = item;
          return acc;
        }, {});

        const metadataFields = ['index', 'key', 'state', 'description'];

        const csvTranslationFields = Object.keys(data[0])
          .filter((field) => !metadataFields.includes(field));

        const lengthErrors = findImportLengthErrors(data);
        if (lengthErrors.length > 0) {
          const noun = lengthErrors.length === 1 ? 'text is' : 'texts are';
          return displayErrorModal({
            title: `${lengthErrors.length} ${noun} too long.`,
            html: (
              <ul className="text-left mb-0 pl-3">
                {lengthErrors.map((item) => {
                  const indexLabel = item.index !== undefined && item.index !== null && item.index !== ''
                    ? `Index ${item.index}, `
                    : '';
                  return (
                    <li key={`${item.key}-${item.language}`}>
                      {`${indexLabel}${item.key} (${item.language}) is ${item.length} characters. Maximum is ${item.maxLength}.`}
                    </li>
                  );
                })}
              </ul>
            ),
          });
        }

        const changesMapState = {}; // Track changes for styling

        const reconstructedLanguages = Object.values(languages).map((lang) => {
          const csvRow = csvLanguageMap[lang.key];
          const updatedLang = { ...lang };

          csvTranslationFields.forEach((field) => {
            if (!changesMapState[lang.key]) changesMapState[lang.key] = {};

            if (csvRow?.[field] !== undefined) {
              if (!lang[field] || lang[field] !== csvRow[field]) {
                changesMapState[lang.key][field] = 'modified'; // Consolidated new + updated (green highlight)
              }
              updatedLang[field] = csvRow[field]; // Keep imported value
            } else if (!lang[field]) {
              // Mark as empty *only if it was empty before* the import
              changesMapState[lang.key][field] = 'empty';
              updatedLang[field] = lang[field] ?? '';
            }
          });


          return updatedLang;
        });

        const handleBatchActions = () => {
          languagesToDelete.forEach(deleteLanguageAction);
          languagesToAdd.forEach(addLanguageAction);
          setLanguagesAction(reconstructedLanguages);
        };

        batch(handleBatchActions);
        setChangesMap(changesMapState);
        return displaySuccessModal({ text: 'Import Successful! Update event to save your changes.' });
      },
    });
  };

  return (
    <ToolkitProvider
      keyField="id"
      data={languagesSortedByIndex}
      columns={columns}
      bootstrap4
      search
      exportCSV={{ fileName: `${slugDate}-languages.csv` }}
    >
      {(props) => (
        <div className={style.container}>
          <div className={style.header}>
            <h3 className="mb-0">Text Settings</h3>
            <div className={style.tableActionsContainer}>
              <div>
                <Button variant="outline-primary" className="rounded-pill" size="sm" onClick={openAddLanguageModalAction}>+ Language</Button>
              </div>
              <div className={style.fileInput}>
                <FormFile
                  as={({ children }) => (
                    <Button variant="outline-primary" className="rounded-pill" size="sm">
                      {children}
                    </Button>
                  )}
                >
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label htmlFor="file-input">Import</label>
                  <FormFile.Input id="file-input" accept="text/csv" onChange={importHandler} />
                </FormFile>
              </div>
              <div className="mr-0">
                <ExportCSVButton {...props.csvProps} className="rounded-pill btn btn-outline-primary btn-sm">Export</ExportCSVButton>
              </div>
            </div>
          </div>
          <BootstrapTable
            {...props.baseProps}
            wrapperClasses="table-responsive searchFilter react-bs-table-card"
            classes="card-table border-top"
            filter={filterFactory()}
            cellEdit={cellEditFactory({ mode: 'dbclick', blurToSave: true })}
            keyField="key"
            defaultSorted={[{ dataField: 'index', order: 'asc' }]}
          />
        </div>
      )}
    </ToolkitProvider>
  );
};

export default connect(
  ({
    languages: { languages, availableLanguages, defaultLanguage },
    events: {
      selectedEvents: [{
        slugDate,
        geofence,
        triviaEnabled,
        shareOptions,
      } = {}],
    },
  }) => ({
    defaultLanguage,
    languages,
    availableLanguages,
    slugDate,
    geofenceEnabled: Boolean(geofence?.enabled),
    triviaEnabled: Boolean(triviaEnabled),
    shareOptions,
  }),
  {
    deleteLanguageAction: deleteLanguage,
    openAddLanguageModalAction: openAddLanguageModal,
    setLanguagesAction: setLanguages,
    addLanguageAction: addLanguage,
  },
)(LanguagesTable);
